import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, ExternalLink, FileText, Image as ImageIcon, DollarSign, User, Calendar, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * Daily Close Review Modal (Angela 2026-05-09).
 * Single-payload read of /dispatch/close-batch-detail. Displays:
 *   - inspector / route_date header
 *   - WhatsApp Proof (open link)
 *   - Route File (open link)
 *   - Day Rate (if applied)
 *   - Closed-by / Closed-at
 *   - Per-order final status table
 *   - Day-level comments
 *   - Order-specific comments grouped by order
 * Read-only except for note delete (Admin only).
 */
const DailyCloseReviewModal = ({ inspectorName, routeDate, currentUserRole, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const reload = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/dispatch/close-batch-detail`, {
        params: { inspector: inspectorName, route_date: routeDate },
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [inspectorName, routeDate]);

  useEffect(() => { reload(); }, [reload]);

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try { await axios.delete(`${API}/dispatch/close-notes/${id}`); await reload(); }
    catch (e) { alert(`Delete failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const cb = data?.closing_batch || {};
  const closings = data?.closings || [];
  const notes = data?.notes || [];
  const dayNotes = notes.filter(n => n.scope === "day");
  const orderNotes = notes.filter(n => n.scope === "order");
  const orderNotesByOrder = orderNotes.reduce((acc, n) => {
    const k = n.order_number || "-";
    (acc[k] = acc[k] || []).push(n); return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="daily-close-review-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-[#2F5D8A]" data-testid="review-title">Daily Close Review</h3>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
              <span className="flex items-center gap-1"><User className="h-3 w-3" /><span data-testid="review-inspector">{inspectorName}</span></span>
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /><span data-testid="review-route-date">{routeDate}</span></span>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="review-close"><X className="h-4 w-4 text-gray-500" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && <p className="text-sm text-gray-400">Loading…</p>}
          {err && <p className="text-sm text-[#C07070]">Error: {err}</p>}
          {!loading && !err && (
            <>
              {/* Top row: proofs + close metadata */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                {/* WhatsApp Proof */}
                <div className="border border-[#6B8F62]/30 bg-[#6B8F62]/5 rounded-lg p-3" data-testid="review-whatsapp-block">
                  <div className="flex items-center gap-2 mb-1.5">
                    <ImageIcon className="h-3.5 w-3.5 text-[#6B8F62]" />
                    <span className="text-[10px] uppercase font-bold text-[#6B8F62] tracking-wider">WhatsApp Proof</span>
                  </div>
                  {cb.whatsapp_proof_url ? (
                    <a href={cb.whatsapp_proof_url} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-[#2F5D8A] hover:underline flex items-center gap-1 break-all"
                       data-testid="review-whatsapp-open">
                      <ExternalLink className="h-3 w-3" />Open
                    </a>
                  ) : <span className="text-[10px] text-gray-400 italic">no proof uploaded</span>}
                </div>
                {/* Route File */}
                <div className="border border-[#7A6B8F]/30 bg-[#7A6B8F]/5 rounded-lg p-3" data-testid="review-route-file-block">
                  <div className="flex items-center gap-2 mb-1.5">
                    <FileText className="h-3.5 w-3.5 text-[#7A6B8F]" />
                    <span className="text-[10px] uppercase font-bold text-[#7A6B8F] tracking-wider">Route File</span>
                  </div>
                  {cb.route_file_url ? (
                    <a href={cb.route_file_url} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-[#2F5D8A] hover:underline flex items-center gap-1 break-all"
                       data-testid="review-route-file-open">
                      <ExternalLink className="h-3 w-3" />Open
                    </a>
                  ) : <span className="text-[10px] text-gray-400 italic">no route file uploaded</span>}
                </div>
                {/* Day Rate + closed metadata */}
                <div className="border border-[#D1A054]/30 bg-[#D1A054]/5 rounded-lg p-3" data-testid="review-meta-block">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="h-3.5 w-3.5 text-[#D1A054]" />
                    <span className="text-[10px] uppercase font-bold text-[#D1A054] tracking-wider">Day Rate</span>
                  </div>
                  <p className="text-sm font-bold text-[#D1A054]" data-testid="review-day-rate">
                    {cb.day_rate_enabled && cb.day_rate_amount
                      ? `$${parseFloat(cb.day_rate_amount).toFixed(2)}`
                      : "—"}
                  </p>
                  <p className="text-[10px] text-gray-500 mt-1.5">
                    Closed by <span className="font-semibold" data-testid="review-closed-by">{cb.closer_name || cb.closed_by || "—"}</span>
                  </p>
                  <p className="text-[10px] text-gray-400" data-testid="review-closed-at">
                    {(cb.created_at || cb.closed_at) ? new Date(cb.created_at || cb.closed_at).toLocaleString() : "—"}
                  </p>
                </div>
              </div>

              {/* Closed orders table */}
              <div className="mb-5" data-testid="review-orders-block">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Closed Orders ({closings.length})</p>
                {closings.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No closing rows yet for this batch.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs" data-testid="review-orders-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Order #</th>
                          <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Final Status</th>
                          <th className="px-2 py-1.5 text-left text-gray-500 font-semibold">Action</th>
                          <th className="px-2 py-1.5 text-right text-gray-500 font-semibold">Pay</th>
                        </tr>
                      </thead>
                      <tbody>
                        {closings.map((c, i) => (
                          <tr key={i} className="border-t border-gray-100" data-testid={`review-order-row-${c.order_number}`}>
                            <td className="px-2 py-1.5 font-mono">{c.order_number || "—"}</td>
                            <td className="px-2 py-1.5">{c.final_status || c.status_at_snapshot || c.status || "—"}</td>
                            <td className="px-2 py-1.5 text-gray-500">{c.action || "—"}</td>
                            <td className="px-2 py-1.5 text-right font-semibold">{c.payment_amount != null ? `$${parseFloat(c.payment_amount).toFixed(2)}` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Day comments */}
              <div className="mb-5" data-testid="review-day-comments-block">
                <p className="text-[10px] font-semibold text-[#2F5D8A] uppercase tracking-wider mb-1.5">Day Comments ({dayNotes.length})</p>
                {dayNotes.length === 0 ? <p className="text-xs text-gray-400 italic">No day comments.</p> : (
                  <ul className="space-y-1">
                    {dayNotes.map(n => (
                      <li key={n.id} className="bg-[#2F5D8A]/5 border border-[#2F5D8A]/20 rounded p-2 text-[11px] flex items-start gap-2 group" data-testid={`review-day-note-${n.id}`}>
                        <span className="flex-1 text-gray-700">{n.comment}</span>
                        <span className="text-[9px] text-gray-400">{(n.created_by || "").split("@")[0]} · {(n.created_at || "").slice(0, 16).replace("T"," ")}</span>
                        {currentUserRole === "Admin" && (
                          <button onClick={() => deleteNote(n.id)} className="text-gray-300 hover:text-[#C07070] opacity-0 group-hover:opacity-100" data-testid={`review-day-note-del-${n.id}`}><Trash2 className="h-3 w-3" /></button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Order-specific comments */}
              <div data-testid="review-order-comments-block">
                <p className="text-[10px] font-semibold text-[#B08A3E] uppercase tracking-wider mb-1.5">Order Comments ({orderNotes.length})</p>
                {orderNotes.length === 0 ? <p className="text-xs text-gray-400 italic">No order-specific comments.</p> : (
                  <div className="space-y-2">
                    {Object.entries(orderNotesByOrder).map(([on, list]) => (
                      <div key={on} className="bg-[#B08A3E]/5 border border-[#B08A3E]/20 rounded p-2" data-testid={`review-order-comments-${on}`}>
                        <p className="text-[10px] font-mono font-bold text-[#B08A3E] mb-1">Order {on}</p>
                        <ul className="space-y-1">
                          {list.map(n => (
                            <li key={n.id} className="text-[11px] flex items-start gap-2 group" data-testid={`review-order-note-${n.id}`}>
                              <span className="flex-1 text-gray-700">• {n.comment}</span>
                              <span className="text-[9px] text-gray-400">{(n.created_by || "").split("@")[0]} · {(n.created_at || "").slice(0, 16).replace("T"," ")}</span>
                              {currentUserRole === "Admin" && (
                                <button onClick={() => deleteNote(n.id)} className="text-gray-300 hover:text-[#C07070] opacity-0 group-hover:opacity-100" data-testid={`review-order-note-del-${n.id}`}><Trash2 className="h-3 w-3" /></button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium rounded-lg" data-testid="review-footer-close">Close</button>
        </div>
      </div>
    </div>
  );
};

export default DailyCloseReviewModal;
